import { ChevronRight, RotateCcw, TrainTrack } from "lucide-react";
import Flag from "react-world-flags";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { RAIL_COUNTRY, RAIL_HINT } from "../../constants/paymentRails.constants";
import type { RailAvailabilityCell } from "../../types/paymentRails.types";
import { formatSince, hasOwnOverride, scopedState } from "./railCellDisplay";

export interface RailAvailabilityCardsProps {
  /** Cells of ONE direction — the page renders a group per section (payins / payouts). */
  cells: RailAvailabilityCell[];
  isLoading: boolean;
  scopeOrgId: string | null;
  orgNameById: Map<string, string>;
  /** True while a write is in flight — the switches lock so a double flip cannot race. */
  isMutating: boolean;
  onToggle: (cell: RailAvailabilityCell, nextEnabled: boolean) => void;
  onClearOverride: (cell: RailAvailabilityCell) => void;
  /** Jumps the page scope to an organization — the overrides list is a shortcut into its view. */
  onViewOrg: (organizationId: string) => void;
}

/**
 * Mobile rendering of one direction's availability rows: one card per rail, never a squeezed table. The
 * reason, the source line and the override list are inline — on touch there is no hover to hide behind — and
 * each override row is a tap-through into that organization's scope.
 */
export function RailAvailabilityCards({
  cells,
  isLoading,
  scopeOrgId,
  orgNameById,
  isMutating,
  onToggle,
  onClearOverride,
  onViewOrg,
}: RailAvailabilityCardsProps) {
  const { t, activeLanguage } = useTranslations("paymentRails");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="h-32 animate-pulse rounded-xl border border-border bg-muted" />
        ))}
      </div>
    );
  }

  if (cells.length === 0) {
    return <ColocaEmpty icon={TrainTrack} title={t("empty_title")} description={t("empty_description")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {cells.map((cell) => {
        const state = scopedState(cell, scopeOrgId);
        const country = RAIL_COUNTRY[cell.rail];
        const sourceKey = !state
          ? "source_default"
          : hasOwnOverride(cell, scopeOrgId)
            ? "source_org_override"
            : scopeOrgId
              ? "source_global"
              : "source_global_setting";
        return (
          <div key={`${cell.rail}:${cell.direction}`} className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              <ColocaBadge variant="secondary" className="font-sans">
                {formatRail(cell.rail)}
              </ColocaBadge>
              {country && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Flag code={country.iso2} className="h-3.5 w-5 shrink-0 rounded object-cover" />
                  {t(country.labelKey)}
                </span>
              )}
              <span className="ml-auto">
                <ColocaStatusPill variant={cell.effective_enabled ? "success" : "danger"} size="sm" withDot>
                  {t(cell.effective_enabled ? "status_enabled" : "status_disabled")}
                </ColocaStatusPill>
              </span>
            </div>
            {RAIL_HINT[`${cell.rail}:${cell.direction}`] && (
              <p className="-mt-1 mb-3 text-xs text-muted-foreground">
                {t(RAIL_HINT[`${cell.rail}:${cell.direction}`])}
              </p>
            )}

            <p className="text-xs text-muted-foreground">
              {t(sourceKey)}
              {state ? ` · ${formatSince(state.since, activeLanguage)}` : ""}
            </p>
            {state?.reason && <p className="mt-1 text-xs text-muted-foreground">{state.reason}</p>}

            {!scopeOrgId && cell.org_overrides.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                <p className="text-xs text-muted-foreground">
                  {t("overrides_count", { count: cell.org_overrides.length })}
                </p>
                {cell.org_overrides.map((override) => (
                  <ColocaButton
                    key={override.organization_id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between gap-2"
                    onClick={() => onViewOrg(override.organization_id)}
                  >
                    <span className="truncate">
                      {orgNameById.get(override.organization_id) ?? override.organization_id}
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <ColocaStatusPill variant={override.enabled ? "success" : "danger"} size="sm" withDot>
                        {t(override.enabled ? "status_enabled" : "status_disabled")}
                      </ColocaStatusPill>
                      <ChevronRight className="size-3.5 text-muted-foreground" />
                    </span>
                  </ColocaButton>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center justify-end gap-2">
              {hasOwnOverride(cell, scopeOrgId) && (
                <ColocaButton
                  variant="ghost"
                  size="xs"
                  icon={RotateCcw}
                  onClick={() => onClearOverride(cell)}
                >
                  {t("action_clear_override")}
                </ColocaButton>
              )}
              <ColocaSwitch
                checked={cell.effective_enabled}
                onCheckedChange={(checked) => onToggle(cell, checked)}
                disabled={isMutating}
                aria-label={`${formatRail(cell.rail)} · ${t(
                  cell.direction === "PAYIN" ? "direction_payin" : "direction_payout",
                )}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
